<template>
    <section>
        <div class="content">
            <h3 class="mt-2 mb-5 ml-2 is-hidden-desktop"><i class="fal fa-file-signature fa-fw mr-3"></i>Documents</h3>

            <div class="content mt-6 mb-6" v-if="loading">
                <h5 class="has-text-grey"><i class="fal fa-tennis-ball fa-spin mr-3"></i>Loading your trip documents. Please wait...</h5>
            </div>
            <div v-else>
                <!-- Documents attached to the trip itself — the same for every
                     traveller, managed by staff, so read-only here. -->
                <template v-if="tripDocuments.length">
                    <div class="columns is-gapless is-mobile">
                        <div class="column is-narrow">
                            <i class="fal fa-file-alt fa-fw is-size-4 mr-3"></i>
                        </div>
                        <div class="column">
                            <h4>Trip Documents</h4>

                            <div class="mt-5 mb-5">
                                <p class="has-text-grey">
                                    General documents for this trip &mdash; these apply to everyone travelling.
                                </p>
                            </div>
                        </div>
                    </div>

                    <registration-document
                        v-for="doc in tripDocuments"
                        :key="'t' + doc.id"
                        :document="doc"
                        @download="downloadTripDocument"
                    />

                    <hr class="mt-6" style="border-bottom: 1px solid #ddd"/>
                </template>

                <div class="columns is-gapless is-mobile" :class="{'mt-6': tripDocuments.length}">
                    <div class="column is-narrow">
                        <i class="fal fa-file-signature fa-fw is-size-4 mr-3"></i>
                    </div>
                    <div class="column">
                        <h4>Your Documents</h4>

                        <div class="mt-5">
                            <p class="has-text-grey">
                                Tickets, vouchers and other paperwork for your registration. You can add your own
                                documents here too &mdash; they stay attached to this trip only.
                            </p>
                            <p class="has-text-grey is-italic" v-if="!documents.length">
                                There are no documents on your registration yet.
                            </p>
                        </div>
                    </div>
                    <div class="column is-narrow ml-5">
                        <b-button type="is-primary" size="is-small" @click="addDocument"><i class="fas fa-plus mr-3"></i>New/Upload</b-button>
                    </div>
                </div>

                <registration-document
                    v-for="doc in documents"
                    :key="doc.id"
                    :document="doc"
                    @download="download"
                    @edit="editDocument"
                    @delete="deleteDocument"
                />

                <!-- Documents attached to individual itinerary events, gathered here
                     so travellers don't have to hunt through the itinerary for a
                     ticket. Read-only — they're managed on the event itself. -->
                <template v-if="eventDocuments.length">
                    <hr class="mt-6" style="border-bottom: 1px solid #ddd"/>

                    <div class="columns mt-6 is-gapless is-mobile">
                        <div class="column is-narrow">
                            <i class="fal fa-calendar-alt fa-fw is-size-4 mr-3"></i>
                        </div>
                        <div class="column">
                            <h4>From Your Itinerary</h4>

                            <div class="mt-5 mb-5">
                                <p class="has-text-grey">
                                    These are attached to specific events and also appear on the day they belong to
                                    in your <router-link :to="{name: 'Itinerary'}">Itinerary</router-link>.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div v-for="doc in eventDocuments" :key="'e' + doc.id" class="columns is-multiline document-container is-mobile">
                        <div class="column is-narrow">
                            <i class="fal has-text-primary fa-calendar-alt is-size-4 pt-2 pb-2 pl-4"></i>
                        </div>
                        <div class="column">
                            <h5 class="pt-2 mb-1">{{doc.subject}}</h5>
                            <div class="mb-4" v-if="doc.id_number">{{doc.id_number}}</div>
                            <div class="notes mb-4 mt-3" v-if="doc.information">{{doc.information}}</div>
                            <b-tag type="is-light" size="is-small">
                                <i class="fas fa-calendar-alt fa-fw mr-2"></i>{{eventLabel(doc)}}
                            </b-tag>
                        </div>
                        <div class="column is-offset-1-desktop is-narrow">
                            <div class="buttons is-right">
                                <b-button v-if="doc.file" type="is-primary" size="is-small" outlined @click="downloadEventDocument(doc)">
                                    <i class="fad fa-file-download mr-2"></i> Download/View
                                </b-button>
                            </div>
                        </div>
                    </div>
                </template>

                <div class="columns mt-6 is-gapless is-mobile" v-else>
                    <div class="column is-narrow">
                        <i class="fal fa-info-circle fa-fw is-size-4 mr-3"></i>
                    </div>
                    <div class="column">
                        <p class="has-text-grey is-italic">
                            Documents and tickets may also be attached directly to events on your
                            <router-link :to="{name: 'Itinerary'}">Itinerary</router-link>.
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <b-modal
            v-model="addDocumentModal"
            trap-focus
            :destroy-on-hide="true"
            close-button-aria-label="Close"
            aria-modal>

            <div class="card">
                <div class="card-header">
                    <p class="card-header-title">
                        <i class="fal fa-file-signature fa-fw mr-3"></i>{{ editingId ? 'Edit Document' : 'Add a New Document' }}
                    </p>
                </div>

                <div class="card-content">
                    <div class="columns">
                        <div class="column">
                            <b-field label="Subject" label-position="inside">
                                <b-input v-model="newDocument.subject" placeholder="ie. Match Tickets, Visa Approval, Transfer Voucher"/>
                            </b-field>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column">
                            <b-field label="Reference/ID Number" label-position="inside">
                                <b-input v-model="newDocument.id_number"/>
                            </b-field>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column">
                            <b-field label="Additional Information" label-position="inside">
                                <b-input type="textarea" v-model="newDocument.information"/>
                            </b-field>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column">
                            <file-pond
                                name="file"
                                ref="pond"
                                label-idle="Drop or click to add document..."
                                :allow-multiple="false"
                                :allowRevert="false"
                                :files="files"
                                @init="handleFilePondInit"
                                @processfile="handleFilePondProcess"
                                :server="{
                                    url: apiUrl,
                                    timeout: 7000,
                                    process: {
                                        url: 'app/registrations/documents/upload',
                                        method: 'POST',
                                        headers: {
                                            'Authorization': token
                                        },
                                        withCredentials: false
                                    }
                                }"
                            />

                            <div class="mt-3">
                                <b-tag type="is-info" size="is-small" v-if="newDocument.file"><i class="fas fa-file fa-fw mr-2"></i>{{newDocument.file_name}}</b-tag>
                            </div>
                        </div>
                    </div>
                </div>

                <footer class="card-footer">
                    <div class="card-footer-item">
                        <b-button type="is-primary" @click="saveDoc" :disabled="!newDocument.subject || processing"><i class="fas mr-3" :class="editingId ? 'fa-check' : 'fa-plus'"></i>{{ editingId ? 'Save Changes' : 'Add Document' }}</b-button>
                    </div>
                </footer>
            </div>

        </b-modal>
    </section>
</template>

<script>
    import {mapGetters} from "vuex";
    import moment from "moment-timezone";
    import {client as http, url} from "../../http_client";
    import RegistrationDocument from "../../components/RegistrationDocument.vue";

    import vueFilePond from 'vue-filepond';
    import 'filepond/dist/filepond.min.css';
    import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css';
    import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
    import FilePondPluginImagePreview from 'filepond-plugin-image-preview';

    const FilePond = vueFilePond(FilePondPluginFileValidateType, FilePondPluginImagePreview);

    export default {
        components: {RegistrationDocument, FilePond},
        data: function() {
            return {
                documents: [],
                tripDocuments: [],
                eventDocuments: [],
                loading: true,
                addDocumentModal: false,
                editingId: null,
                newDocument: {
                    file: null
                },
                processing: false,
                files: [],
                apiUrl: url
            };
        },
        computed: {
            ...mapGetters(['trip', 'registration', 'token'])
        },
        methods: {
            load: function(force) {
                // registration is {} until the store resolves; the watch below
                // re-fires this once it does.
                if (!this.registration.id) return;

                http.get('/app/registrations/' + this.registration.id + '/documents', {force: force}).then(response => {
                    this.documents = response.data.documents;
                    this.tripDocuments = response.data.trip_documents || [];
                    this.eventDocuments = response.data.event_documents;
                    this.loading = false;
                });
            },
            eventLabel: function(doc) {
                let day = doc.event_day ? moment(doc.event_day).format('MMM Do') : null;
                return day ? `${doc.event_title} — ${day}` : doc.event_title;
            },
            // Event documents keep their existing download route, which resolves the
            // file against every event this registration can see.
            downloadEventDocument: function(doc) {
                window.open(`${this.apiUrl}app/registrations/${this.registration.id}/documents/file/${doc.file}?auth=${this.token}`, '_blank');
            },
            // Opens in a new tab, so the JWT goes on the query string rather than an
            // Authorization header. The API checks the document belongs to this
            // registration and is visible to this traveller before serving it.
            download: function(doc) {
                window.open(`${this.apiUrl}app/registrations/${this.registration.id}/documents/${doc.id}/file?auth=${this.token}`, '_blank');
            },
            // Trip documents are fetched through the traveller's registration so
            // the same ownership check applies server-side.
            downloadTripDocument: function(doc) {
                window.open(`${this.apiUrl}app/registrations/${this.registration.id}/trip-documents/${doc.id}/file?auth=${this.token}`, '_blank');
            },
            addDocument: function() {
                this.editingId = null;
                this.newDocument = {file: null};
                this.files = [];
                this.addDocumentModal = true;
            },
            editDocument: function(doc) {
                this.editingId = doc.id;
                this.newDocument = {...doc};
                this.files = [];
                this.addDocumentModal = true;
            },
            deleteDocument: function(doc) {
                this.$buefy.dialog.confirm({
                    title: 'Delete Document',
                    message: `Are you sure you want to delete <b>${doc.subject}</b>? This can't be undone.`,
                    confirmText: 'Delete',
                    type: 'is-danger',
                    hasIcon: true,
                    onConfirm: () => {
                        http.delete('/app/registrations/' + this.registration.id + '/documents/' + doc.id, {force: true}).then(() => {
                            this.$notify({
                                group: 'notify',
                                type: 'success',
                                title: 'Document Deleted',
                                text: 'The document has been removed.'
                            });
                            this.load(true);
                        });
                    }
                });
            },
            saveDoc: function() {
                if (!this.processing) {
                    this.processing = true;

                    let base = '/app/registrations/' + this.registration.id + '/documents';
                    let request = this.editingId
                        ? http.put(base + '/' + this.editingId, this.newDocument, {force: true})
                        : http.post(base, this.newDocument, {force: true});

                    request.then(response => {
                        this.$notify({
                            group: 'notify',
                            type: 'success',
                            title: this.editingId ? 'Document Updated' : 'Document Added',
                            text: this.editingId ? 'Your document has been updated.' : 'Your document has been added.'
                        });
                        this.addDocumentModal = false;
                        this.load(true);
                        setTimeout(() => {
                            this.processing = false;
                        }, 3500);
                    });
                }
            },
            handleFilePondInit: function() {
            },
            handleFilePondProcess: function(err, response) {
                this.processing = true;

                let file = JSON.parse(response.serverId);

                this.newDocument.file = file.file_name;
                this.newDocument.file_name = file.file_original_name;
                this.newDocument.file_mime = file.file_mime;

                setTimeout(function() {
                    this.$refs.pond.removeFile(file.id);
                    this.processing = false;
                }.bind(this), 3000);
            }
        },
        watch: {
            registration: function() {
                this.load(true);
            }
        },
        mounted() {
            this.load();
        }
    };
</script>

<style lang="scss" scoped>
    // Matches RegistrationDocument's card so the itinerary rows below read as
    // part of the same list.
    .document-container {
        margin: 0.05px 0.05px 1rem;
        background-color: white;
        border-radius: 0.3rem;
        position: relative;
        padding: 0.05px;

        @media only screen and (max-width: 768px) {
            h4 {
                font-size: 1rem;
            }
        }

        .notes {
            border: 1px solid #eee;
            border-radius: 8px;
            padding: 0.5rem;
            font-size: 0.9rem;
            margin-left: -8px;
        }
    }
</style>
